/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useState } from 'react';

import { Bell, Check, CheckCheck, X } from 'lucide-react';

import { Avatar } from '../../primitives/Avatar';
import { Badge } from '../../primitives/Badge';
import { Button } from '../../primitives/Button';
import { Drawer } from '../../primitives/Drawer';
import { IconButton } from '../../primitives/IconButton';
import { cn } from '../../utils';

export interface Notification {
  id: string;
  title: string;
  description?: string;
  timestamp: Date;
  isRead: boolean;
  type?: 'info' | 'success' | 'warning' | 'error';
  avatar?: string;
  avatarInitials?: string;
}

export interface NotificationPanelLabels {
  title?: string;
  markAsRead?: string;
  markAllAsRead?: string;
  close?: string;
  all?: string;
  unread?: string;
  noNotifications?: string;
  allRead?: string;
  willAppear?: string;
  viewAll?: string;
  timeNow?: string;
  timeMinutes?: string;
  timeHours?: string;
  timeDays?: string;
}

const defaultLabels: Required<NotificationPanelLabels> = {
  title: 'Notificaciones',
  markAsRead: 'Marcar como leída',
  markAllAsRead: 'Marcar todas como leídas',
  close: 'Cerrar',
  all: 'Todas',
  unread: 'Sin leer',
  noNotifications: 'No hay notificaciones',
  allRead: 'Todas las notificaciones han sido leídas',
  willAppear: 'Las notificaciones aparecerán aquí',
  viewAll: 'Ver todas las notificaciones',
  timeNow: 'Ahora',
  timeMinutes: 'Hace {{count}}m',
  timeHours: 'Hace {{count}}h',
  timeDays: 'Hace {{count}}d',
};

export interface NotificationPanelProps {
  notifications: Notification[];
  unreadCount?: number;
  onMarkAsRead?: (id: string) => void;
  onMarkAllAsRead?: () => void;
  onNotificationClick?: (notification: Notification) => void;
  labels?: NotificationPanelLabels;
}

type TabType = 'all' | 'unread';

function formatTimestamp(date: Date, labels: Required<NotificationPanelLabels>): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return labels.timeNow;
  if (minutes < 60) return labels.timeMinutes.replace('{{count}}', String(minutes));
  if (hours < 24) return labels.timeHours.replace('{{count}}', String(hours));
  if (days < 7) return labels.timeDays.replace('{{count}}', String(days));
  return date.toLocaleDateString();
}

function NotificationItem({
  notification,
  onMarkAsRead,
  onClick,
  labels,
}: {
  notification: Notification;
  onMarkAsRead?: (id: string) => void;
  onClick?: (notification: Notification) => void;
  labels: Required<NotificationPanelLabels>;
}) {
  return (
    <div
      className={cn(
        'flex gap-3 p-3 rounded-lg transition-colors cursor-pointer',
        notification.isRead ? 'bg-tkf-surface' : 'bg-primary-50',
        'hover:bg-tkf-surface-muted'
      )}
      onClick={() => onClick?.(notification)}
    >
      {/* Avatar or Icon */}
      <div className="flex-shrink-0">
        {notification.avatar || notification.avatarInitials ? (
          <Avatar src={notification.avatar} initials={notification.avatarInitials} size="sm" />
        ) : (
          <div
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full',
              notification.type === 'success' && 'bg-success-100 text-success-600',
              notification.type === 'warning' && 'bg-warning-100 text-warning-600',
              notification.type === 'error' && 'bg-error-100 text-error-600',
              (!notification.type || notification.type === 'info') &&
                'bg-primary-100 text-primary-600'
            )}
          >
            <Bell className="h-5 w-5" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={cn('text-sm', notification.isRead ? 'text-tkf-fg' : 'font-medium text-tkf-fg')}
        >
          {notification.title}
        </p>
        {notification.description && (
          <p className="mt-0.5 text-xs text-tkf-fg-muted line-clamp-2">
            {notification.description}
          </p>
        )}
        <p className="mt-1 text-xs text-tkf-fg-subtle">
          {formatTimestamp(notification.timestamp, labels)}
        </p>
      </div>

      {/* Mark as Read */}
      {!notification.isRead && onMarkAsRead && (
        <button
          type="button"
          className="flex-shrink-0 p-1 text-tkf-fg-subtle hover:text-primary-600 transition-colors"
          onClick={e => {
            e.stopPropagation();
            onMarkAsRead(notification.id);
          }}
          aria-label={labels.markAsRead}
        >
          <Check className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export function NotificationPanel({
  notifications,
  unreadCount,
  onMarkAsRead,
  onMarkAllAsRead,
  onNotificationClick,
  labels: labelsProp,
}: NotificationPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('all');

  const labels = { ...defaultLabels, ...labelsProp };
  const count = unreadCount ?? notifications.filter(n => !n.isRead).length;

  const filteredNotifications =
    activeTab === 'all' ? notifications : notifications.filter(n => !n.isRead);

  return (
    <>
      {/* Trigger Button */}
      <IconButton
        variant="ghost"
        size="md"
        badge={count > 0 ? count : undefined}
        onClick={() => setIsOpen(true)}
        aria-label={labels.title}
        animated
      >
        <Bell className="h-5 w-5" />
      </IconButton>

      {/* Notifications Drawer */}
      <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)} position="right" size="lg">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-tkf-line px-4 py-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-tkf-fg">{labels.title}</h2>
              {count > 0 && (
                <Badge variant="primary" size="sm">
                  {count}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {count > 0 && onMarkAllAsRead && (
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onMarkAllAsRead}
                  aria-label={labels.markAllAsRead}
                >
                  <CheckCheck className="h-5 w-5" />
                </IconButton>
              )}
              <IconButton
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                aria-label={labels.close}
              >
                <X className="h-5 w-5" />
              </IconButton>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-tkf-line">
            <button
              type="button"
              className={cn(
                'flex-1 px-4 py-2.5 text-sm font-medium transition-colors',
                activeTab === 'all'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-tkf-fg-muted hover:text-tkf-fg'
              )}
              onClick={() => setActiveTab('all')}
            >
              {labels.all} ({notifications.length})
            </button>
            <button
              type="button"
              className={cn(
                'flex-1 px-4 py-2.5 text-sm font-medium transition-colors',
                activeTab === 'unread'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-tkf-fg-muted hover:text-tkf-fg'
              )}
              onClick={() => setActiveTab('unread')}
            >
              {labels.unread} ({count})
            </button>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {filteredNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-tkf-surface-muted">
                  <Bell className="h-8 w-8 text-tkf-fg-subtle" />
                </div>
                <p className="text-sm font-medium text-tkf-fg">{labels.noNotifications}</p>
                <p className="mt-1 text-xs text-tkf-fg-muted">
                  {activeTab === 'unread' ? labels.allRead : labels.willAppear}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-tkf-line-light p-2">
                {filteredNotifications.map(notification => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={onMarkAsRead}
                    onClick={onNotificationClick}
                    labels={labels}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-tkf-line p-4">
              <Button variant="outline" className="w-full">
                {labels.viewAll}
              </Button>
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
}
